import { Arr, Store } from '@deqo/foundation';
import { alea } from 'seedrandom';
import { shuffle } from '../../../CodesForFoundation';
import { round } from '../../../platform/geom/Math';

export type PollType = 'single' | 'multi' | 'scale';

export class PollSpec {
    public static from([type, question, ...options]: string[]) {
        if (!type || !question || options.length < 2) return null;
        if (type !== 'single' && type !== 'multi' && type !== 'scale') return null;
        return new PollSpec(type, question, options);
    }
    constructor(
        public readonly type: PollType,
        public readonly question: string,
        public readonly options: string[],
    ) { }
}

export type PollSpecCallback = (spec: PollSpec) => void;

export type LivePollState = PollSpec & { id: string, active: boolean };
export type LivePoll = Store<LivePollState>;

export class PollResultItem {
    constructor(
        readonly option: string,
        readonly color: string,
        readonly format: '/' | '%',
        readonly numer: number,
        readonly denom: number,
    ) { }
}

const pollColors = [
    '#3f83f8',
    '#0e9f6e',
    '#6875f5',
    '#ff5a1f',
    '#e74694',
    '#9061f9',
    '#f05252',
    '#0694a2',
    '#c27803',
];

export class PollResults {

    public static readonly empty = new PollResults([], []);

    public static from(poll: LivePoll | LivePollState): PollResults {
        if ('getState' in poll) return PollResults.from(poll.getState());
        const rand = alea(poll.id ?? '', { global: true });
        const colors = shuffle(pollColors.slice(), rand);
        const ignore = ['id', 'active', 'type', 'question', 'options'];
        const voters = Object.keys(poll).filter(x => !ignore.includes(x));
        const votes = voters.flatMap(x => poll[x]);
        const items = poll.type === 'scale' ? (
            [new PollResultItem(
                'Avg Score',
                colors[0],
                '/',
                round(votes.reduce((t, x) => t + x, 0) / votes.length, 2),
                parseInt(poll.options[1]) || 1,
            )]
        ) : (
            poll?.options?.map((o, i) => {
                const c = Arr.at(colors, i);
                const t = votes.filter(x => x === i).length;
                return new PollResultItem(o, c, '%', t, votes.length);
            })
        );
        return new PollResults(voters, items ?? []);
    }

    private constructor(
        public readonly voters: string[],
        public readonly items: PollResultItem[],
    ) { }
}
